import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface CountryRow {
  state: string;
  count: number;
  amount: number;
}

interface State {
  countryStateData: any;
  countryGridData: any;
  isLoading: boolean;
  error?: string;
  legendValues: any | any | null;
  aggregated: any | null;
  gridLatLng: any | null;
  selectedGeometry: any | null;

  countryTableRows: CountryRow[] | null;
}

const initialState: State = {
  isLoading: false,
  countryStateData: null,
  countryGridData: null,
  legendValues: null,
  aggregated: null,
  gridLatLng: null,
  selectedGeometry: null,
  countryTableRows: null,
};

const pickLegend = (payload: any) =>
  payload?.min_max ??
  payload?.min_max_values ??
  payload?.minMax ??
  payload?.legend ??
  payload?.result?.min_max ??
  payload?.result?.min_max_values ??
  payload?.result?.minMax ??
  payload?.result?.legend ??
  payload?.payload?.min_max ??
  payload?.payload?.min_max_values ??
  payload?.payload?.minMax ??
  payload?.payload?.legend ??
  null;

const pickDataArray = (payload: any): any[] =>
  (Array.isArray(payload?.data) && payload.data) ||
  (Array.isArray(payload?.result?.data) && payload.result.data) ||
  (Array.isArray(payload?.payload?.data) && payload.payload.data) ||
  [];

const pickAggregated = (payload: any): any =>
  payload?.aggregated ??
  payload?.result?.aggregated ??
  payload?.payload?.aggregated ??
  null;

/** Parse level2 string/array to tokens (lowercased) */
const parseL2ToTokens = (l2: any): string[] => {
  const raw = Array.isArray(l2) ? l2.join(" ") : String(l2 || "");
  return raw
    .toLowerCase()
    .split(/[,\s/&|+]+/)
    .filter(Boolean);
};

/** Build [{ state, count, amount }] for Claims Country table (RRC) */
const buildCountryTableRows = (payload: any): CountryRow[] => {
  const data = pickDataArray(payload);
  const rows: Record<string, { count: number; amount: number }> = {};

  for (const row of data) {
    // Accept multiple variants of state name
    const state: string =
      row?.vstate ??
      row?.state ??
      row?.state_name ??
      row?.stateName ??
      "Unknown";

    // Count: prefers claims_count/count; fallback 1 if rows are per-claim
    const count: number = Number(row?.claims_count ?? row?.count ?? 1) || 0;

    // Amount: sum array first; else numeric fallbacks
    const arr = Array.isArray(row?.payout_amounts) ? row.payout_amounts : null;
    let amt = 0;
    if (arr) {
      amt = arr.reduce((s: number, v: any) => s + (Number(v) || 0), 0);
    } else {
      const fallback =
        row?.payout_amount ??
        row?.paid_amount ??
        row?.amount ??
        row?.amount_paid ??
        0;
      amt = Number(fallback) || 0;
    }

    if (!rows[state]) rows[state] = { count: 0, amount: 0 };
    rows[state].count += count;
    rows[state].amount += amt;
  }

  return Object.entries(rows).map(([state, agg]) => ({
    state,
    count: agg.count,
    amount: Number(agg.amount.toFixed(2)),
  }));
};

const countryApiSlice = createSlice({
  name: "country",
  initialState,
  reducers: {
    startLoading(state: State) {
      state.isLoading = true;
    },
    stopLoading(state: State) {
      state.isLoading = false;
    },
    hasError(state: State, action: PayloadAction<string>) {
      state.isLoading = false;
      state.error = action.payload;
    },

    /* ---------------- Country → State-wise ---------------- */
    handleCountryStateDetails(state: State, action: PayloadAction<any>) {
      state.isLoading = false;
      const payload = action.payload ?? {};
      state.countryStateData = payload?.data ?? null;
      state.legendValues = pickLegend(payload);
      state.aggregated = payload?.aggregated ?? null;

      const l1 = String(payload?.__level1 || "");
      const l2 = String(payload?.__level2 || "");
      const view = String(payload?.__view || "");
      const targetL2 = new Set([
        "Rejected",
        "Repudiated",
        "CWP",
        "Payment Not Initiated",
      ]);

      if (view === "country" && l1 === "Claims" && targetL2.has(l2)) {
        state.countryTableRows = buildCountryTableRows(payload);
      } else {
        state.countryTableRows = null; // Not applicable
      }
    },
    handleCountryGridDetails(state: State, action: PayloadAction<any>) {
      state.isLoading = false;
      const payload = action.payload ?? {};
      state.countryGridData = payload?.data ?? null;
      state.legendValues = pickLegend(payload);
      state.aggregated = payload?.aggregated ?? null;
      state.countryTableRows = null; // Not for grid view
    },

    /* ---------------- Map helpers ---------------- */
    handleCountryGridLatLng(state: State, action: PayloadAction<any>) {
      state.gridLatLng =
        action.payload && action.payload?.features?.[0]
          ? action.payload?.features[0]
          : null;
      state.selectedGeometry =
        action.payload?.features?.[0]?.geometry?.coordinates?.[0]?.[0] || null;
    },

    handleCountrySelectedGeometry(state: State, action: PayloadAction<any>) {
      state.selectedGeometry = action.payload;
    },
  },
});

export const {
  startLoading,
  stopLoading,
  hasError,
  handleCountryStateDetails,
  handleCountryGridDetails,
  handleCountryGridLatLng,
  handleCountrySelectedGeometry,
} = countryApiSlice.actions;

export default countryApiSlice.reducer;
